import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import StatusDot from "./StatusDot";
import { ToastContext, type ShowToast, type ToastInput, type ToastTone } from "../hooks/useToast";

/**
 * Renders the toast stack and hands `useToast` down. Mounted once, in `App`.
 *
 * See `hooks/useToast.ts` for why this exists at all. What lives here is only
 * the parts that need the DOM: where the stack sits, how long each message
 * stays, and how it leaves.
 */

/** How long a message stays, when the caller does not say. */
const DURATION_MS: Record<ToastTone, number> = {
  ok: 4000,
  info: 5000,
  danger: 7000,
};

/**
 * A toast offering an action is a question, and a question that closes itself
 * while the cursor is still travelling towards it was never really asked.
 */
const WITH_ACTION_MS = 9000;

/** Matches the leave transition below, so the node is removed after it fades. */
const LEAVE_MS = 180;

/**
 * Older messages drop off the top rather than growing a wall. Three is the
 * point where the newest one starts being pushed out of the corner of the eye.
 */
const MAX_VISIBLE = 3;

interface LiveToast extends ToastInput {
  id: number;
  tone: ToastTone;
  durationMs: number;
  leaving: boolean;
}

export default function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<LiveToast[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      window.setTimeout(() => remove(id), LEAVE_MS);
    },
    [remove],
  );

  const show = useCallback<ShowToast>((input) => {
    const tone = input.tone ?? "danger";
    const toast: LiveToast = {
      ...input,
      tone,
      id: nextId.current++,
      durationMs:
        input.durationMs ??
        (input.action ? Math.max(DURATION_MS[tone], WITH_ACTION_MS) : DURATION_MS[tone]),
      leaving: false,
    };
    setToasts((list) => {
      // A repeat of the same key replaces its predecessor in place, so a
      // stuck backend does not build a tower of identical complaints.
      const deduped = input.key ? list.filter((t) => t.key !== input.key) : list;
      return [...deduped, toast].slice(-MAX_VISIBLE);
    });
  }, []);

  // `show` is stable by construction (no deps), so publishing it straight as
  // the context value means a toast never re-renders the app underneath it.
  return (
    <ToastContext value={show}>
      {children}
      {/* `pointer-events-none` on the frame, restored per card: the strip spans
          the viewport and would otherwise eat clicks on everything beneath it.
          Bottom padding clears the phone tab bar via the same token the page
          scroller uses, so the two can never disagree about its height. */}
      <div
        className="jv-pad-tabbar pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-stretch gap-2 px-3 sm:items-end sm:px-6 md:pb-0"
        aria-live="polite"
        aria-relevant="additions"
      >
        <div className="flex flex-col items-stretch gap-2 pb-3 sm:items-end sm:pb-6">
          {toasts.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>
      </div>
    </ToastContext>
  );
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: LiveToast;
  /** Stable across renders, so a new toast does not restart this one's clock. */
  onDismiss: (id: number) => void;
}) {
  const [held, setHold] = useState(false);
  const close = useCallback(() => onDismiss(toast.id), [onDismiss, toast.id]);

  // The clock stops while the pointer or the keyboard is on the card: reaching
  // for «Повернути» must not be a race against the thing you are reaching for.
  // Leaving restarts it in full rather than resuming, which only ever gives the
  // reader more time than they asked for.
  useEffect(() => {
    // Already on its way out — re-arming here would queue a second removal.
    if (held || toast.leaving) return;
    const timer = window.setTimeout(close, toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [held, toast.leaving, close, toast.durationMs]);

  return (
    <div
      // Failures are announced over whatever the reader is doing; a confirmation
      // waits its turn in the polite queue on the wrapper.
      role={toast.tone === "danger" ? "alert" : "status"}
      onMouseEnter={() => setHold(true)}
      onMouseLeave={() => setHold(false)}
      onFocusCapture={() => setHold(true)}
      onBlurCapture={() => setHold(false)}
      // Arrives by `jv-toast-in`, leaves by transition. The entrance is CSS on
      // purpose — see the keyframes in index.css for what depending on
      // requestAnimationFrame here cost.
      className={`jv-elevated jv-toast-in pointer-events-auto flex w-full items-start gap-2.5 p-3 transition-[opacity,transform] duration-200 ease-drawer sm:w-96 ${
        toast.leaving ? "translate-y-2 opacity-0" : ""
      }`}
    >
      <StatusDot tone={toast.tone} className="mt-1 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm break-words text-fg">{toast.message}</p>
        {toast.detail && (
          <p className="text-micro break-words text-muted">{toast.detail}</p>
        )}
      </div>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action?.onSelect();
            close();
          }}
          className="shrink-0 rounded-control px-1.5 py-0.5 text-sm font-medium text-accent transition-colors hover:bg-accent/15"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={close}
        aria-label="Сховати"
        className="shrink-0 rounded-control px-1.5 py-0.5 text-sm text-muted transition-colors hover:text-fg"
      >
        ×
      </button>
    </div>
  );
}
